import { useSessionStorage } from '@vueuse/core'
import { computed, type ComputedRef } from 'vue'
import type { Database } from '@gat-ship/types'

function mapToAvailableDatabaseLocalStorage(
  db: Database,
  isSelected: boolean
): Database {
  return {
    ...db,
    isSelected
  }
}

// Constants for session storage keys
export const DATABASE_STORAGE_KEYS = {
  AVAILABLE_DATABASES: 'available-databases'
} as const

/**
 * Centralized composable for managing database storage.
 *
 * This composable provides a consistent interface for accessing and managing
 * available databases, selection state, and related utilities across the
 * application.
 *
 * The pre-promotion `fetchDatabases()` and `getTenantId()` methods used to
 * live here, but they pulled the storage primitive into a bidirectional
 * cluster with `@gat-ship/user` and `@gat-ship/support-session`. The
 * platform-session-cluster-promotion plan (DL-23) moved both responsibilities
 * to the orchestrator (`apps/gs-cloud/src/session-bootstrap.ts`), leaving
 * this composable as a pure storage primitive with no feature dependencies.
 *
 * @returns An object containing reactive database storage references and utilities
 */
export function useDatabaseSessionStorage() {
  // Reactive session storage for available databases
  const _databases = useSessionStorage<Database[]>(
    DATABASE_STORAGE_KEYS.AVAILABLE_DATABASES,
    []
  )

  // Computed property to get the currently selected database
  const selectedDatabase: ComputedRef<Database | undefined> = computed(() => {
    return _databases.value.find((db) => db.isSelected)
  })

  // Computed property to get the default database
  const defaultDatabase: ComputedRef<Database | undefined> = computed(() => {
    return _databases.value.find((db) => db.isDefault)
  })

  // Computed property to check if user has access to multiple databases
  const hasMultipleDatabases: ComputedRef<boolean> = computed(() => {
    return _databases.value.length > 1
  })

  // Computed property to get databases affected by support session
  const supportSessionDatabases: ComputedRef<Database[]> = computed(() => {
    return _databases.value.filter((db) => db.affectedBySupportSession)
  })

  // Computed property to get all database info in a structured way
  const databaseInfo = computed(() => {
    return {
      current: selectedDatabase.value,
      all: _databases.value,
      support: supportSessionDatabases.value,
      default: defaultDatabase.value
    }
  })

  /**
   * Sets the available databases in storage.
   *
   * @param databases - Array of databases to store
   */
  function setDatabases(databases: Omit<Database, 'isSelected'>[]) {
    const updatedDatabases = databases.map((db) => ({
      ...db,
      isSelected:
        _databases.value.find((existing) => existing.id === db.id)
          ?.isSelected ?? false
    }))
    _databases.value = updatedDatabases
  }

  function updateDatabaseSelection(databaseId: string) {
    const databases = _databases.value.map((db) =>
      mapToAvailableDatabaseLocalStorage(db, db.id === databaseId)
    )
    _databases.value = databases
  }

  /**
   * Selects a specific database by ID and deselects all others.
   *
   * @param databaseId - The ID of the database to select
   * @returns The selected database or undefined if not found
   */
  function selectDatabase(databaseId: string): Database | undefined {
    const database = _databases.value.find((db) => db.id === databaseId)
    if (!database) {
      throw new Error(
        'Unable to select database - please contact admin or Gatship support.'
      )
    }
    updateDatabaseSelection(databaseId)
    return database
  }

  /**
   * Gets the selected database ID, with fallback logic.
   *
   * Priority order:
   * 1. Currently selected database (session continuity)
   * 2. User-preferred default database (cross-session, if still granted)
   * 3. Backend-designated default database
   * 4. First available database
   *
   * @param userPreferredDefaultId - The user's saved default database ID from user state.
   *   Skipped when empty or when the database is no longer in the granted list.
   * @returns The database ID. Throws an error if no database is available.
   */
  function getSelectedDatabaseId(userPreferredDefaultId?: string): string {
    // First check if there is a selected database
    const selected = selectedDatabase.value
    if (selected) {
      return selected.id
    }

    // Check user-preferred default (only if the database is still granted)
    if (userPreferredDefaultId) {
      const preferred = _databases.value.find(
        (db) => db.id === userPreferredDefaultId
      )
      if (preferred) {
        selectDatabase(preferred.id)
        return preferred.id
      }
    }

    // If no selected database, check if there is a default database
    const defaultDb = defaultDatabase.value
    if (defaultDb) {
      selectDatabase(defaultDb.id)
      return defaultDb.id
    }

    // If no selected or default database, select the first database
    const firstDb = _databases.value[0]
    if (firstDb) {
      selectDatabase(firstDb.id)
      return firstDb.id
    }

    throw new Error('No available database - please contact admin.')
  }

  /**
   * Finds and selects the default or first support session database.
   * Used when in support session mode.
   *
   * @returns The database ID or undefined if no support databases available
   */
  function getDefaultSupportSessionDatabaseId(): string | undefined {
    const supportDbs = supportSessionDatabases.value

    if (supportDbs.length === 0) {
      return undefined
    }

    // Try to find default support database first
    const defaultSupportDb = supportDbs.find((db) => db.isDefault)
    if (defaultSupportDb) {
      selectDatabase(defaultSupportDb.id)
      return defaultSupportDb.id
    }

    // Fall back to first support database. The length check above
    // guarantees this exists; `!` satisfies noUncheckedIndexedAccess.
    const firstSupportDb = supportDbs[0]!
    selectDatabase(firstSupportDb.id)
    return firstSupportDb.id
  }

  /**
   * Clears all database storage.
   */
  function clearDatabases() {
    _databases.value = []
  }

  function removeDatabase(databaseId: string) {
    const databases = [..._databases.value].filter((db) => db.id !== databaseId)
    setDatabases(databases)
  }

  function addDatabase(database: Database) {
    const databases = [..._databases.value, database]
    setDatabases(databases)
  }

  const hasDatabaseAccess = computed(() => {
    return _databases.value.length > 0
  })

  return {
    // Reactive references
    availableDatabases: _databases,
    // Computed properties
    selectedDatabase,
    defaultDatabase,
    hasMultipleDatabases,
    supportSessionDatabases,
    databaseInfo,
    hasDatabaseAccess,
    // Utility functions
    setDatabases,
    selectDatabase,
    getSelectedDatabaseId,
    getDefaultSupportSessionDatabaseId,
    clearDatabases,
    // Storage keys
    STORAGE_KEYS: DATABASE_STORAGE_KEYS,
    addDatabase,
    removeDatabase
  }
}
